import React, { Component } from 'react';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import config from '../../../config';
import { ephotobookUpdateAccess } from '../../Common/Providers/EPhotobookApi';
import { HTMLTrans, withTranslationProvider } from '../../Common/Translations';
import { publicAccess, restrictedAccess, ephotobookAccessData } from './EphotobookAccess';
import { willShowModal } from '../../Common/Modal/Modal';
import { PRIVATE, RESTRICTED, PUBLIC, OWNER } from './Constants';
import EPhotobookBuyThis from './EPhotobookBuyThis';
import { connectUserHeaderinfo } from '../../Auth/WithUserHeaderInfo';

const Action = ({ label, onClick, disabled = false }) => {
  return <a
    href="javascript:void(0);"
    onClick={disabled ? e => (e.preventDefault()) : onClick }
    className={`text-primary text-nowrap pr-3 ${disabled ? 'text-gray-lighten-2' : ''}`}>
    <u>{label}</u>
  </a>;
};

const handleView = (file) => {
  window.location.href = `${window.location.origin}/ephotobook/preview?file=${file}`;
}

const PopModalPreviewShare = willShowModal({ size: 'md' })(Action);

const ModalContent = compose(
  withStateHandlers(({
    selectedAccessType = {
      code: PRIVATE,
      emails: []
    },
    fileLink,
    deletedEmails = [],
    newEmails = [],
    countryCode = config.storeCode,
    disabled = true,
  }) => ({
    fileLink,
    selectedAccessType,
    deletedEmails,
    newEmails,
    countryCode,
    disabled,
  }), {
    onClickDeletedEmails: ({ deletedEmails }) => ( value ) => ({
      deletedEmails : deletedEmails.concat(value),
    }),
    onEmailChange: () => ({ target }) => ({ 
      newEmails: target.value,
      disabled: false
    }),
    updateAccessType: ({ selectedAccessType }) => (value) => ({
      selectedAccessType: {
        ...selectedAccessType,
        code: value
      },
    }),
    updateEmails: ({ selectedAccessType }) => (value) => ({
      newEmails: [],
      disabled: true,
      selectedAccessType: {
        ...selectedAccessType,
        emails: value
      },
    }),
  }),
  withHandlers({
    onSubmit: props => () => {
      const data = ephotobookAccessData(props);
      const { updateEmails } = props;
      ephotobookUpdateAccess(data)
        .done(({ data: { share }}) => {
          updateEmails(share);
        });
    },
  }),
  withHandlers({
    saveOnChange: ({ updateAccessType, onClickDeletedEmails }) => (value) => {
      const isSelectedAccess = [PRIVATE, RESTRICTED, PUBLIC];
      if (isSelectedAccess.includes(value)) {
        updateAccessType(value);
      } else {
        onClickDeletedEmails(value);
      }
    },
  }),
  lifecycle({ 
    componentDidUpdate(prevProps) {
      const { 
        updateEmails,
        onSubmit,
        deletedEmails,
        selectedAccessType: {
          code,
          emails
        }
      } = this.props;

      if (emails === undefined) {
        updateEmails(prevProps.selectedAccessType.emails);
      }

      if (prevProps.selectedAccessType.code !== code  ) {
        onSubmit();
      }

      if (deletedEmails !== prevProps.deletedEmails) {
        emails.filter(email => !deletedEmails.includes(email));
        onSubmit();
      }
    }
  })
)(({
  closeModal,
  onSubmit,
  fileLink: { link },
  title,
  previewImg,
  newEmails,
  onEmailChange,
  selectedAccessType: { code, emails },
  t,
  index,
  saveOnChange,
  disabled,
  currentPage,
  isConfirmationPage
}) => {
  return (
  <div>
    <div className="modal-content">
      <div className="modal-header align-items-start">
        <h3 className="text-primary font-weight-bold">
          <HTMLTrans id='account.ephotobook_share_title'>Share your ePhotobook</HTMLTrans>
        </h3>
      </div>
      <div className="modal-body pt-0">
        <div className="d-flex flex-column pb-2">
          <label className="d-flex justify-content-start ProximaNova-Bold">
            <HTMLTrans id="account.ephotobook_share_permission">Permission</HTMLTrans>
          </label>
          <label className="custom-control">
            <input
              type="radio"
              className="with-gap bg-primary-darken-1"
              onChange={() => saveOnChange(PRIVATE)}
              checked={code === PRIVATE}
              value={PRIVATE}
            />
            <span>{t("account.ephotobook_accesstype_private", "Private (Only me)")}</span>
          </label>
          { !isConfirmationPage && 
            (
              <div>
                <label className="custom-control">
                  <input
                    type="radio"
                    className="with-gap bg-primary-darken-1"
                    onChange={() => saveOnChange(RESTRICTED)}
                    checked={code === RESTRICTED}
                    value={RESTRICTED}
                  />
                  <span>{t("account.ephotobook_accesstype_restricted", "Restricted (Only invited people can view this project)")}</span>
                </label>
              </div>
            )
          }
          <label className="custom-control">
            <input
              type="radio"
              className="with-gap bg-primary-darken-1"
              onChange={() => saveOnChange(PUBLIC)}
              checked={code === PUBLIC}
              value={PUBLIC}
            />
            <span>{t("account.ephotobook_accesstype_public", "Public (Anyone with this link can view this project)")}</span>
          </label>
        </div>
        { code === RESTRICTED ?
            restrictedAccess({t, newEmails,  onEmailChange, onSubmit, disabled, index}) : code === PUBLIC ?
            publicAccess({t, link, currentPage, index, title, previewImg}) : null }
        <div className='modal-footer p-0'>
          <button
            type="button"
            className="btn btn-block btn-primary"
            data-dismiss='modal'
            onClick={closeModal}>
            <HTMLTrans id="account.ephotobookshare_button_done">Done</HTMLTrans>
          </button>
        </div>
      </div>
    </div>
    <div className="modal-content mt-1" style={{ display: code === RESTRICTED ? 'block' : 'none' }}>
      <div id="accordion_faq" className="modal-header align-items-start accordion subscription-page__faq" style={{ display: "block"}}>
        <a className="collapsed" data-toggle="collapse" data-target={`#shared-email-list--page-${currentPage}-${index}`} href={`#shared-email-list-page-${currentPage}-${index}`} aria-expanded="true">
          <h3 className="ProximaNova-Bold">
            <HTMLTrans id="account.ephotobook_sharedwithemail_title">Shared With</HTMLTrans>
          </h3>
        </a>
        <hr className="bg-gray-lighten-2 my-3 w-100"/>
        <div id={`shared-email-list-page-${currentPage}-${index}`} className="collapse fixed-content show" data-parent="#accordion_shared_email">
          { emails && emails.map((email, i) => (
            <div key={`list-email-${i}`}>
              <div className="row col">
                <div className="col-10">
                  <p id={`email_${i}`}>{email}</p>
                </div>
                <div className="col-2">
                  <button type='button' className='close' onClick={() => saveOnChange(email)}>
                    <p aria-hidden='true'>&times;</p>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)});

class EphotobookShareModal extends Component {
  render() {
    const {
      item,
      index,
      t,
      currentPage,
      currentSelectedType = OWNER,
      userHeaderInfo,
      isConfirmationPage = false
    } = this.props;

    const accessType = item.access_type;
    const fileLink = item.url;
    const title = item.title;
    const previewImg = item.preview_img;
    const isLoggedIn = userHeaderInfo.isLoggedIn;
    const purchasable = item.purchasable;

    return (
      <div className='d-flex flex-wrap'>
        <div className={`d-flex align-items-center ${!isConfirmationPage ? `col-sm-4` : 'h4 mb-0'}`}>
          {
            !isConfirmationPage &&
              <Action
                label={<HTMLTrans id='account.ephotobook_preview'>Preview</HTMLTrans>}
                onClick={() => { handleView(fileLink.file) }}
              />
          }
          {currentSelectedType === OWNER &&
            <PopModalPreviewShare size='md' label={t("account.ephotobook_share_btn", "Share")}>
            {closeModal =>
              <ModalContent
                index={index}
                currentPage={currentPage}
                selectedAccessType={accessType}
                fileLink={fileLink}
                title={title}
                previewImg={previewImg}
                closeModal={closeModal}
                t={t}
                isConfirmationPage={isConfirmationPage}
              />}
            </PopModalPreviewShare>
          }
        </div>
        { 
          purchasable && 
            <div className='col-sm-4'>
              <EPhotobookBuyThis isLoggedIn={isLoggedIn} file={fileLink.file} page="account"/>
            </div>
        }
      </div>
    );
  }
}

const enhance = compose(
  withTranslationProvider,
  connectUserHeaderinfo
);

export default enhance(EphotobookShareModal);
export const undecorated = EphotobookShareModal;



// WEBPACK FOOTER //
// ./src/Account/EPhotobook/EphotobookShareModal.js